import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import useWindowWidth from '../hooks/useWindowWidth';
import { PRIMARY } from '../styles/shared';
import Logo from './Logo';
import { HamburgerIcon, CloseIcon, SparkleIcon } from './Icons';

const styles = {
  nav: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding: '0 2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '60px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  logo: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: PRIMARY,
    textDecoration: 'none',
    letterSpacing: '-0.02em',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  link: (active) => ({
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: active ? PRIMARY : '#64748b',
    backgroundColor: active ? '#f0fdfa' : 'transparent',
    transition: 'all 0.15s',
  }),
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  userName: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: '500',
  },
  logoutBtn: {
    padding: '0.4rem 0.9rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: '#64748b',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
};

export default function NavBar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useWindowWidth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav aria-label="Main navigation" style={{
      ...styles.nav,
      padding: isMobile ? '0 1rem' : '0 2rem',
    }}>
      <Link to="/dashboard" style={{ textDecoration: 'none' }}><Logo size="small" /></Link>

      {!isMobile && (
        <div style={styles.navLinks}>
          <Link to="/dashboard" style={styles.link(isActive('/dashboard'))}>Dashboard</Link>
          <Link to="/journal" style={styles.link(isActive('/journal'))}>Journal</Link>
          <Link to="/ai-tools" style={{ ...styles.link(isActive('/ai-tools')), display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <SparkleIcon size={16} color={isActive('/ai-tools') ? PRIMARY : '#eab308'} />AI Tools
          </Link>
          <Link to="/profile" style={styles.link(isActive('/profile'))}>Profile</Link>
        </div>
      )}

      <div style={{
        ...styles.rightSection,
        gap: isMobile ? '0.5rem' : '1rem',
      }}>
        {user && !isMobile && (
          // T35: "Feedback" was previously rendered here next to the username.
          // It was a utility action mixed with identity controls; moved to a
          // floating button (see FeedbackFloatingButton component) so it's
          // discoverable from every page without crowding the identity area.
          <span style={styles.userName}>
            <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}>
              {user.name}
            </span>
          </span>
        )}
        {isMobile && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            {/* M4 (T42): Hamburger trigger sized to 44x44 minimum tap target. */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              style={{
                ...styles.logoutBtn,
                minHeight: '44px',
                minWidth: '44px',
                padding: '0.5rem 0.75rem',
                fontSize: '1.2rem',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {menuOpen ? <CloseIcon size={20} /> : <HamburgerIcon size={20} />}
            </button>
            {menuOpen && (
              <div role="menu" style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
                borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '220px', padding: '0.5rem 0', zIndex: 200,
              }}>
                {user && (
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', fontSize: '0.8rem', color: '#94a3b8' }}>
                    {user.name}
                  </div>
                )}
                {/* T35: "Feedback" removed from the mobile menu list — it now
                    lives in the floating button so it's never buried inside an
                    expandable menu. */}
                {[
                  { path: '/dashboard', label: 'Dashboard' },
                  { path: '/journal', label: 'Journal' },
                  { path: '/ai-tools', label: 'AI Tools' },
                  { path: '/profile', label: 'Profile' },
                ].map(item => (
                  // M4 (T42): each menu row ≥44px tall via minHeight+flex centering.
                  <Link
                    key={item.path}
                    to={item.path}
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center',
                      minHeight: '44px', padding: '0.6rem 1rem',
                      textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500',
                      color: isActive(item.path) ? PRIMARY : '#1e293b',
                      backgroundColor: isActive(item.path) ? '#f0fdfa' : 'transparent',
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '0.25rem', paddingTop: '0.25rem' }}>
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    style={{
                      display: 'flex', alignItems: 'center', width: '100%',
                      minHeight: '44px', padding: '0.6rem 1rem',
                      textAlign: 'left', background: 'none', border: 'none',
                      fontSize: '0.95rem', fontWeight: '500', color: '#ef4444',
                      cursor: 'pointer',
                    }}
                  >
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {!isMobile && (
          <button style={{
            ...styles.logoutBtn,
            padding: '0.5rem 0.9rem',
            fontSize: '0.875rem',
            minHeight: '36px',
          }} onClick={handleLogout}>
            Logout
          </button>
        )}
      </div>
      {/* T35: Floating Feedback pill. Rendered alongside the nav so it appears
          on every authed page (NavBar is included on every authed page).
          position: fixed keeps it pinned regardless of scroll; the auth check
          on `user` matches the rest of NavBar so it only renders when signed
          in (signed-out pages don't include this NavBar component anyway). */}
      {user && <FeedbackFloatingButton currentPath={location.pathname} />}
    </nav>
  );
}

// T35: Small floating teal pill bottom-right. Stays out of the way of primary
// content but is discoverable from every screen. Hidden when already on the
// /contact page so we don't render a "Feedback" link that targets the page
// the user is currently looking at.
function FeedbackFloatingButton({ currentPath }) {
  if (currentPath === '/contact' || currentPath.startsWith('/contact/')) return null;
  return (
    <Link
      to="/contact"
      aria-label="Send feedback"
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        zIndex: 50,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.55rem 1rem',
        backgroundColor: PRIMARY,
        color: '#ffffff',
        borderRadius: '9999px',
        textDecoration: 'none',
        fontSize: '0.85rem',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(1,105,111,0.35)',
        border: '1px solid rgba(255,255,255,0.15)',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '0.95rem', lineHeight: 1 }}>{'\u{1F4AC}'}</span>
      <span>Feedback</span>
    </Link>
  );
}
